import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import Image from '@components/Image';
import RenderAnimation from '@components/RenderAnimation';
import AnchorLink from '@components/AnchorLink';
import { QUIZ_URL } from '@constants/routes';
import { buildContentfulImageURL } from '@utils/image';
import Button from '@/src/components/Button';
import quizRingAnimation from '../../../../animations/purpleRing.json';
import star from '../../imgs/star.svg';
import s from './QuizSplash.module.scss';
import splashBg from '../../imgs/quiz-splash-bg.jpg';
import splashBg2x from '../../imgs/quiz-splash-bg@2x.jpg';

const propTypes = {
  level: PropTypes.string,
  title: PropTypes.string,
  summary: PropTypes.string,
  date: PropTypes.string,
  questionCount: PropTypes.number,
  quizStart: PropTypes.func,
  coverImage: PropTypes.string,
  isLoading: PropTypes.bool,
  buttonText: PropTypes.oneOf(['start quiz', 'resume quiz']),
  isEvent: PropTypes.bool,
  eventWeek: PropTypes.string,
};

const QuizSplash = ({
  level = '',
  title = '',
  summary = '',
  date = '',
  questionCount = null,
  quizStart = () => {},
  coverImage = '',
  isLoading = false,
  buttonText = 'start quiz',
  isEvent = false,
  eventWeek = null,
}) => {
  const [starLevel, setStarLevel] = useState([]);

  const formattedDate = date ? format(new Date(date), 'do LLL yyyy') : '';
  const coverImageSet = [
    {
      src: `${buildContentfulImageURL(coverImage, {
        width: 325,
        height: 325,
        fit: 'fill',
        focus: 'top',
        format: 'webp',
      })} 1x, ${buildContentfulImageURL(coverImage, {
        width: 650,
        height: 650,
        fit: 'fill',
        focus: 'top',
        format: 'webp',
      })} 2x`,
      type: 'image/webp',
    },
    {
      src: `${buildContentfulImageURL(coverImage, {
        width: 325,
        height: 325,
        fit: 'fill',
        focus: 'top',
        format: 'jpg',
      })} 1x, ${buildContentfulImageURL(coverImage, {
        width: 650,
        height: 650,
        fit: 'fill',
        focus: 'top',
        format: 'jpg',
      })} 2x`,
      type: 'image/jpeg',
    },
    {
      src: buildContentfulImageURL(coverImage, {
        width: 325,
        height: 325,
        fit: 'fill',
        focus: 'top',
        format: 'jpg',
      }),
    },
  ];

  useEffect(() => {
    const starArray = [];
    const difficultyLevel = {
      easy: 1,
      medium: 2,
      difficult: 3,
    };
    for (let i = 0; i < difficultyLevel[level.toLowerCase()]; i += 1) {
      starArray.push(<Image imageSet={[{ src: star }]} contain alt="star" />);
    }
    setStarLevel(starArray);
  }, [level, setStarLevel]);

  const CoverImage = () => {
    return (
      <div className={s.coverImage}>
        <RenderAnimation
          className={s.purpleRingAnimation}
          ariaLabel="quiz ring animation"
          animationData={quizRingAnimation}
          loop
        />
        <Image imageSet={coverImageSet} cover className={s.innerImage} alt="quiz cover image" />
      </div>
    );
  };

  return (
    <div
      className={s.root}
      style={{
        '--splashBg': `url(${splashBg})`,
        '--splashBg2x': `url(${splashBg2x})`,
      }}
    >
      <div className={[s.columnLeft, isEvent && eventWeek ? s.hasEvent : ''].join(' ')}>
        {isEvent && eventWeek && <div className={s.eventWeek}>{eventWeek}</div>}
        <CoverImage />
      </div>
      <div className={s.columnRight}>
        <div className={s.level}>LEVEL</div>
        <div className={s.stars}>
          {starLevel.map((starImage) => {
            return (
              <div key={`starImage${Math.random()}`} className={s.star}>
                {starImage}
              </div>
            );
          })}
        </div>
        {isEvent && eventWeek && <div className={s.eventWeek}>{eventWeek}</div>}
        <CoverImage />
        <div className={s.title}>{title}</div>
        <div className={s.dateAndCount}>
          <div className={s.date}>{formattedDate}</div>
          <div className={s.pipe}>|</div>
          <div className={s.questionCount}>{`${questionCount} Questions`}</div>
        </div>
        <div className={s.summary}>{summary}</div>
        <div className={s.startMoreBtns}>
          <Button
            label={buttonText}
            tabIndex="-1"
            loading={isLoading}
            disabled={isLoading}
            onClick={quizStart}
            mb="25px"
            className={s.startResume}
          />
          {!isEvent && (
            <AnchorLink href={QUIZ_URL} title="More Quizzes">
              <Button
                type="text"
                label="More Quizzes"
                tabIndex="-1"
                mt="30px"
                className={s.moreQuizzes}
              />
            </AnchorLink>
          )}
        </div>
      </div>
    </div>
  );
};

QuizSplash.propTypes = propTypes;
export default QuizSplash;
